import React from "react";
import { Link } from "react-router-dom";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { ArrowRight } from "lucide-react";

export function About() {
  return (
    <PageShell>
      {/* HERO */}
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="OUR STORY" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <SectionLabel>Our Story</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.9] tracking-tighter" data-testid="about-hero-heading">
            We started <br /> because <span className="hl-coral">no one else would.</span>
          </h1>
          <div className="mt-12 grid md:grid-cols-2 gap-12 max-w-5xl">
            <p className="text-white/85 text-lg leading-relaxed">
              anArtizen began quietly — as conversations between practitioners and the people who hire them. It became clear very quickly that India does not lack creative talent. It lacks the connective tissue between the person doing the work and the person paying for it.
            </p>
            <p className="text-white/85 text-lg leading-relaxed">
              So we&apos;re building it, one honest piece at a time. Right now, that means three things you can actually use — a growth session called Unfair Advantage, a no-pressure learning drop, and a build room where brands, artists and institutions come to us with a project and leave with a plan.
            </p>
          </div>
          <Link to="/manifesto" data-testid="about-manifesto-cta" className="btn-pill btn-coral mt-12">Read The Manifesto <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid md:grid-cols-[220px_1fr] gap-10 items-start">
          <div>
            <p className="label-mono !text-primary mb-3">— FOUNDER</p>
            <p className="font-display text-4xl md:text-5xl font-bold leading-[0.95] tracking-tight" data-testid="about-founder-name">Meher</p>
            <p className="meta-mono mt-3 !text-white/60">MUMBAI</p>
          </div>
          <div className="max-w-3xl">
            <p className="text-white/85 text-lg leading-relaxed" data-testid="about-founder-note">
              anArtizen is founded and run by Meher — after more than a decade of working with artists, brands and cultural institutions across India, and watching brilliant creative practitioners repeatedly get the short end of the stick on rates, credit and clarity.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-6">
              The company is deliberately small right now. Everything you see on this site is what we&apos;re actually doing — no roadmap dressed up as a product, no promises we can&apos;t keep. When we launch more, you&apos;ll hear it here first.
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

export function Manifesto() {
  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="THE MANIFESTO" />
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-20 pb-24">
          <SectionLabel>The Manifesto</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.9] tracking-tighter">How we <span className="hl-cyan">work with you.</span></h1>
          <p className="mt-8 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
            Not a mission statement. A short list of commitments we&apos;ll hold ourselves to on every project — brand, artist, or institution.
          </p>
          <ol className="mt-14 space-y-8 list-none">
            {[
              "Every artist is a business. We treat them like one.",
              "A brief is not a template. Every project is scoped from zero.",
              "We charge for the work. We never charge for the pitch.",
              "Credit is currency. Everyone who did the work gets named.",
              "Rates are a floor, not a ceiling. Discounts come from us, not from the artist.",
              "We reply in days, not weeks — even to say no.",
              "Culture is made in years. We plan for decade one, not quarter one.",
              "If we&apos;re not the right fit, we say so — and hand you a name that is.",
            ].map((line, i) => (
              <li key={i} className="grid grid-cols-[60px_1fr] gap-6 items-start">
                <span className="num-display text-3xl font-bold text-primary">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-display text-2xl md:text-3xl font-bold leading-[1.1] tracking-tight" dangerouslySetInnerHTML={{ __html: line }} />
              </li>
            ))}
          </ol>
        </div>
      </section>
    </PageShell>
  );
}
